import React from 'react';
import _ from 'lodash';
import { Button, Select } from 'design-system/Form';
import CreatableSelect from 'react-select-3/creatable';
import { ValueType } from 'react-select-3';
import apiFetch from '@scale/frontend-lib/api-fetch';
import { ReviewLevel } from '@scale/shared/defaults';
import SelectInstructionItem, { ISelectItem } from 'corp/components/UI/SelectInstructionItem';
import {
  IInstructionItemJson,
  IInstructionItemWithMinReviewLevelJson,
  isInstructionItemWithMinReviewLevelJson,
} from '@scale/shared/types/instructionitem';
import { makeStyles } from 'design-system/styles';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';

interface IFilteringSectionProps {
  filterItems: string[];
  isEditing: boolean;
  canEdit: boolean;
  handleEdit: (errorTypes?: string[]) => void;
  placeholder: string;
  label?: string;
}

interface ISelectOption {
  label: string;
  value: string;
}

const createOption = (label: string): ISelectOption => ({
  label,
  value: label,
});

export const FilteringSection = ({
  filterItems = [],
  canEdit,
  isEditing,
  handleEdit,
  placeholder,
  label,
}: IFilteringSectionProps) => {
  const [newFilterValue, setNewFilterValue] = React.useState('');
  const [filterValues, setFilterValues] = React.useState<ISelectOption[]>(
    filterItems.map(createOption) || [],
  );
  React.useEffect(() => {
    canEdit && handleEdit(filterValues?.map((f: ISelectOption) => f.value) || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValues, canEdit]);
  const handleChange = (filterValues: ISelectOption[]) => {
    setFilterValues(filterValues || []);
  };
  const handleKeyDown = (event: React.KeyboardEvent<Element>) => {
    if (!newFilterValue) return;
    switch (event.key) {
      case 'Enter':
      case 'Tab':
        setFilterValues([...filterValues, createOption(newFilterValue)]);
        setNewFilterValue('');
        event.preventDefault();
    }
  };
  if (isEditing || filterItems.length) {
    return (
      <>
        {label && (
          <div style={{ marginTop: 12 }}>
            <strong>{label}</strong>
          </div>
        )}
        <div style={{ flexGrow: 1 }}>
          <CreatableSelect
            components={{
              DropdownIndicator: null,
            }}
            inputValue={newFilterValue}
            isClearable
            isMulti
            menuIsOpen={false}
            onChange={(selectedOption: ValueType<ISelectOption>) =>
              handleChange(selectedOption as ISelectOption[])
            }
            onInputChange={setNewFilterValue}
            // @ts-ignore react-select-3 uses older (non-breaking) KeyboardEvent version
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            value={filterValues}
          />
        </div>
      </>
    );
  }
  return null;
};

interface IInstructionItemsEditorProps {
  handleEdit(items: (IInstructionItemJson | IInstructionItemWithMinReviewLevelJson | any)[]): void;
  instructionItems: (IInstructionItemJson | IInstructionItemWithMinReviewLevelJson | any)[];
  isCreatable: boolean;
  withReviewLevel: boolean;
  query: Record<string, string>;
  canEditIIFilters: boolean;
  withEnablement?: boolean;
  omitCourseFilter?: boolean;
}

const ALLOWED_MIN_REVIEW_LEVELS = [ReviewLevel.Normal, ReviewLevel.Level1, ReviewLevel.Corp];

const InstructionItemsEditor = ({
  handleEdit,
  instructionItems,
  withReviewLevel,
  query,
  isCreatable,
  canEditIIFilters,
  withEnablement = false,
  omitCourseFilter,
}: IInstructionItemsEditorProps) => {
  const [editingFilterTypeOnII, setEditingFilterTypeOnII] = React.useState<string | undefined>();
  const { selectedProject, isRemoAdmin } = useRemoAdminCtx();

  const handleAddInstructionItem = () => {
    handleEdit(_.concat(instructionItems, {}));
  };

  const handleCreateInstructionItem = async (item: ISelectItem) => {
    const name = item.value;

    if (!name) {
      return;
    }
    const { instructionItem } = await apiFetch(null, 'corp-api/instructionitem/create', {
      method: 'POST',
      body: { name },
    });
    handleEditInstructionItems(instructionItems.length - 1, instructionItem);
  };

  const handleEditInstructionItems = (
    pIndex: number,
    updateVal?: IInstructionItemJson | IInstructionItemWithMinReviewLevelJson,
    minReviewLevel?: ReviewLevel,
  ) => {
    if (!updateVal) {
      return;
    }
    const newInstructionItemName = isInstructionItemWithMinReviewLevelJson(updateVal)
      ? updateVal.instructionItem.name
      : updateVal.name;

    const existingIndex = instructionItems
      .map(i => (isInstructionItemWithMinReviewLevelJson(i) ? i.instructionItem.name : i.name))
      .indexOf(newInstructionItemName);
    if (existingIndex !== -1 && existingIndex !== pIndex) {
      window.alert(`An instruction item named ${newInstructionItemName} already exists`);
      return;
    }
    const newInstructionItem =
      !isInstructionItemWithMinReviewLevelJson(updateVal) && withReviewLevel
        ? { minReviewLevel, instructionItem: updateVal }
        : updateVal;
    const newInstructionItems = _.cloneDeep(instructionItems);
    newInstructionItems[pIndex] = newInstructionItem;
    handleEdit(newInstructionItems);
  };

  const handleDeleteInstructionItem = (pIndex: number) => {
    const newInstructionItems = _.cloneDeep(instructionItems);
    newInstructionItems.splice(pIndex, 1);
    handleEdit(newInstructionItems);
  };

  const handleEnablementInstructionItem = async (pIndex: number) => {
    const iiId = instructionItems[pIndex]._id;

    const currentEnableState = instructionItems[pIndex].enabled;
    const newInstructionItems = _.cloneDeep(instructionItems);
    if (currentEnableState === false) {
      // currently disabled, re-enable
      newInstructionItems[pIndex].enabled = true;
    } else {
      // blank or enabled, disable
      newInstructionItems[pIndex].enabled = false;
    }

    const newEnableState = newInstructionItems[pIndex].enabled;

    await apiFetch(null, 'corp-api/instructionitem/enablement', {
      method: 'POST',
      body: { iiId, newEnableState },
    });
    handleEdit(newInstructionItems);
  };

  const classes = useStyles();

  return (
    <>
      <div>{instructionItems.length > 8 ? 'Warning: over 8 instruction items' : ''}</div>
      <div className={classes.instrItemEditorWrapper}>
        {/* eslint-disable-next-line complexity */}
        {_.map(instructionItems, (pII, i) => {
          const p = pII.instructionItem || pII;
          return (
            <div className={classes.instrItem} key={i}>
              <div className={classes.instrItemNameId}>
                {p.name || (
                  <SelectInstructionItem
                    query={query}
                    id={p.name}
                    projectId={(isRemoAdmin && selectedProject) || query.project}
                    omitCourseFilter={omitCourseFilter || !!query.benchmarkProject}
                    instructionItems={instructionItems}
                    isCreatable={isCreatable}
                    onChange={g => handleEditInstructionItems(i, g)}
                    onNewOptionClick={handleCreateInstructionItem}
                  />
                )}
                {p._id && (
                  <>
                    <br />
                    <small>{p._id}</small>
                  </>
                )}
                {isInstructionItemWithMinReviewLevelJson(pII) && withReviewLevel && (
                  <div style={{ flexGrow: 1, alignItems: 'center', display: 'flex' }}>
                    <small>Minimum Review Level</small>
                    <Select
                      value={pII.minReviewLevel}
                      name="minReviewLevel"
                      onChange={e =>
                        handleEditInstructionItems(
                          i,
                          p,
                          e.target.value === 'None'
                            ? undefined
                            : (parseInt(e.target.value) as ReviewLevel),
                        )
                      }
                    >
                      <option value="None">All Attempters/Reviewers</option>
                      {ALLOWED_MIN_REVIEW_LEVELS.map(reviewLevel => (
                        <option value={reviewLevel} key={reviewLevel}>
                          {reviewLevel}
                        </option>
                      ))}
                    </Select>
                  </div>
                )}
                <FilteringSection
                  canEdit={canEditIIFilters}
                  isEditing={
                    canEditIIFilters &&
                    (editingFilterTypeOnII === pII._id ||
                      pII.errorTypes?.length ||
                      pII.graderTypes?.length)
                  }
                  handleEdit={graderTypes =>
                    !!pII && handleEditInstructionItems(i, { ...pII, graderTypes })
                  }
                  placeholder="Filter II scores by grader types"
                  label="Grader Types"
                  filterItems={pII.graderTypes}
                />
                <FilteringSection
                  canEdit={canEditIIFilters}
                  isEditing={
                    canEditIIFilters &&
                    (editingFilterTypeOnII === pII._id ||
                      pII.errorTypes?.length ||
                      pII.graderTypes?.length)
                  }
                  handleEdit={errorTypes =>
                    !!pII && handleEditInstructionItems(i, { ...pII, errorTypes })
                  }
                  placeholder="Filter II scores by error type"
                  label="Error Types"
                  filterItems={pII.errorTypes}
                />
                {canEditIIFilters &&
                  editingFilterTypeOnII !== pII._id &&
                  !pII.errorTypes?.length &&
                  !pII.graderTypes?.length && (
                    <div style={{ flexGrow: 1, paddingTop: 4 }}>
                      <a onClick={() => setEditingFilterTypeOnII(pII._id)}>
                        Add Grader/Error Type Filters
                      </a>
                    </div>
                  )}
              </div>
              <div className={classes.instrItemButtons}>
                {withEnablement && (
                  <div>
                    <Button size="sm" onClick={() => handleEnablementInstructionItem(i)}>
                      {instructionItems[i].enabled === false ? (
                        <div>Enable</div>
                      ) : (
                        <div>Disable</div>
                      )}
                    </Button>
                  </div>
                )}
                <div style={{ fontWeight: 'bold' }}>
                  <Button red={true} size="sm" onClick={() => handleDeleteInstructionItem(i)}>
                    X
                  </Button>
                </div>
              </div>
            </div>
          );
        })}

        <div className={classes.addInstrItem}>
          <div>
            <Button onClick={handleAddInstructionItem}>Add New Instruction Item</Button>
          </div>
        </div>
      </div>
    </>
  );
};

const useStyles = makeStyles({
  addInstrItem: {
    margin: 8,
    width: '280px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  instrItemNameId: {
    flexGrow: 1,
    paddingRight: 4,
    fontSize: '13px',
    overflowWrap: 'anywhere',
  },
  instrItemButtons: {
    '& > div': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
    },
  },
  instrItem: {
    margin: 8,
    padding: 8,
    background: 'var(--color-RemoGray01)',
    borderRadius: 'var(--border-radius)',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    minWidth: '300px',
    cursor: 'pointer',
    border: '1px solid #ccc',
    width: '80%',
  },
  instrItemEditorWrapper: {
    padding: 8,
    display: 'flex',
    flexWrap: 'wrap',
    marginRight: 32,
    flexShrink: 0,
    width: '100%',
    justifyContent: 'center',
  },
});

InstructionItemsEditor.defaultProps = {
  isCreatable: true,
  withReviewLevel: false,
  canEditIIFilters: false,
  query: {},
};

export default InstructionItemsEditor;
